import {
  Button,
  DetailRow,
  Disclaimer,
  Div,
  DuplicateStoreConnectionMeta,
  formatDateTimeShort,
  lightTheme,
  Modal,
  P,
  Separator,
  SystemProblem,
} from '@vgrubs/components';
import {
  problemAction,
  problemTypeLabel,
} from '../helpers/systemProblemLabels';
import { useLocationPhones } from '../hooks/useLocationPhones';
import { useResolveSystemProblem } from '../hooks/useSystemProblem';
import { ConnectionMap } from './ConnectionMap';
import { InPlaceActions, ProblemActionButton } from './ProblemActionButton';
import { ProblemDetails } from './ProblemDetails';
import { ProblemNextStep } from './ProblemNextStep';
import { SystemProblemPill } from './SystemProblemPill';

type Props = {
  isOpen: boolean;
  problem: SystemProblem | null;
  onClose: () => void;
  /** See ProblemActionButton. */
  inPlace?: InPlaceActions;
};

/**
 * One problem, and what to do about it.
 *
 * Laid out like the admin's other detail modals: where it stands first, then
 * what is left to do (while open), the facts, and the backend's own account.
 * A duplicate draws its store and brands (ConnectionMap); every other type
 * lists its facts (ProblemDetails). At the foot, the problem's own action
 * beside Mark as Resolved. Every admin and support user may resolve any
 * problem - the list is shared, nobody owns a row.
 */
export const SystemProblemModal = ({
  isOpen,
  problem,
  onClose,
  inPlace,
}: Props) => {
  const { busy, resolve } = useResolveSystemProblem(problem?.id ?? null);
  const calls = !!problem && problemAction(problem)?.kind === 'call';
  const phoneOf = useLocationPhones(
    calls && problem?.locationId ? [problem.locationId] : []
  );

  if (!problem) return null;

  const duplicate =
    problem.type === 'duplicateStoreConnection' && problem.meta
      ? (problem.meta as DuplicateStoreConnectionMeta)
      : null;
  const resolved = problem.status === 'resolved';
  const phone = calls ? phoneOf(problem.locationId) : null;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      logo={false}
      header={problem.title}
      subtitle={`${problemTypeLabel(problem.type)} · #${problem.id}`}
      textAlign="left"
      width="560px"
      // The title names the device and the location, so on a phone it wraps
      // to three lines and the header grows past its usual 80px; the body
      // gives that room back so the whole modal still fits the screen.
      maxHeightSM="calc(90vh - 140px)"
    >
      <Div $flex $between $alignCenter $gap="16px">
        <SystemProblemPill problem={problem} />
        <Disclaimer $m="0" $right $color={lightTheme.lightGray}>
          Detected {formatDateTimeShort(problem.createdAt)}
        </Disclaimer>
      </Div>

      {/* Only while open: once resolved, what was left to do is history. */}
      {!resolved && (
        <>
          <Separator $m="16px 0" />
          <ProblemNextStep problem={problem} />
        </>
      )}

      {/* The picture before the prose: which store, which brands, and which
          connection is the problem, readable at a glance. */}
      {duplicate ? (
        <>
          <Separator $m="16px 0" />
          <P $m="0">Where the store is connected</P>
          <ConnectionMap problem={problem} meta={duplicate} />
        </>
      ) : (
        <ProblemDetails problem={problem} phone={phone} />
      )}

      <Separator $m="16px 0" />
      <DetailRow label="What happened">
        <P $m="0" $left>
          {problem.description}
        </P>
      </DetailRow>

      {resolved && (
        <>
          <Separator $m="16px 0" />
          <DetailRow
            label="Resolved"
            hint={problem.resolvedAt && formatDateTimeShort(problem.resolvedAt)}
          >
            {problem.autoResolved
              ? 'Automatically - the problem went away'
              : `By ${problem.resolvedByName ?? 'unknown'}`}
          </DetailRow>
        </>
      )}

      {!resolved && (
        <Div $flex $gap="12px" $m="24px 0 0" $columnSM>
          {/* Sized by the row, not by the button's own small width. */}
          <Div $flex $flexNone $widthSM="100%">
            <ProblemActionButton
              problem={problem}
              phone={phone}
              wide
              inPlace={inPlace}
            />
          </Div>
          <Button
            $selected
            $w="100%"
            $wSM="100%"
            $loading={busy}
            disabled={busy}
            onClick={resolve}
          >
            Mark as Resolved
          </Button>
        </Div>
      )}
    </Modal>
  );
};
